// app/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import { ForwardedRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { IProps } from './confirmYourActivityModal';

interface IState {
  remainingSeconds: number;
}

const DEFAULT_REMAINING_SECONDS = 60;

/**
 * Runs the countdown behind the session activity confirmation modal. Starts a one-second interval decrementing remainingSeconds while the modal is open and resets it to the default when closed or unmounted, exposes clearTimer through the imperative ref, shows the session-timed-out modal once the countdown reaches zero, and stops the timer when the user confirms.
 *
 * @param open - Whether the confirmation modal is currently displayed.
 * @param handleConfirmClickProp - Parent callback invoked when the user confirms activity.
 * @param handleSessionTimedOutModalDisplay - Parent callback that displays the session-timed-out modal.
 * @param ref - Forwarded ref exposing clearTimer to the parent.
 * @returns The countdown state and the confirm handler.
 */
export const useConfirmYourActivityModal = (
  {
    open,
    handleConfirmClick: handleConfirmClickProp,
    handleSessionTimedOutModalDisplay,
  }: Pick<IProps, 'open' | 'handleConfirmClick' | 'handleSessionTimedOutModalDisplay'>,
  ref: ForwardedRef<unknown>,
) => {
  const [state, setState] = useSetState<IState>({
    remainingSeconds: DEFAULT_REMAINING_SECONDS,
  });

  const interval = useRef<NodeJS.Timeout | null>(null);

  /**
   * Confirms the user is still active and stops the session expiry countdown. Clears the interval held in the ref then invokes the parent's confirm callback.
   *
   * @returns Nothing.
   */
  const handleConfirmClick = () => {
    clearInterval(interval.current ?? undefined);
    handleConfirmClickProp();
  };

  useImperativeHandle(ref, () => ({
    clearTimer: () => {
      clearInterval(interval.current ?? undefined);
      setState({ remainingSeconds: DEFAULT_REMAINING_SECONDS });
    },
  }));

  useEffect(() => {
    if (open) {
      interval.current = setInterval(() => {
        setState((prevState) => ({
          remainingSeconds: prevState.remainingSeconds - 1,
        }));
      }, 1000);
    } else {
      setState({ remainingSeconds: DEFAULT_REMAINING_SECONDS });
    }
    return () => {
      clearInterval(interval.current ?? undefined);
      setState({ remainingSeconds: DEFAULT_REMAINING_SECONDS });
    };
  }, [open]);

  useUpdateEffect(() => {
    if (state.remainingSeconds <= 0) {
      handleSessionTimedOutModalDisplay();
    }
  }, [state.remainingSeconds]);

  return { state, handleConfirmClick };
};
